import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Dashboard from "@/pages/Dashboard";
import {
  ScanLine, PackagePlus, UserCog, Radio, ArrowLeftRight, Boxes,
  Users, ShieldAlert, RotateCcw, Bell, RefreshCw, ArrowRight, ShieldCheck,
} from "lucide-react";

/* -------------------------------------------------------------------------
   Employee Cockpit — the BoxHero staff landing "command center".
   Big touch tiles to launch portals + aggregated analytics + a live
   "things to do" side panel across all customers.
   ------------------------------------------------------------------------- */

const TILES = [
  { key: "receiving", label: "Launch Returns", sub: "Scan & process returns", icon: ScanLine, to: "/receiving", accent: "orange", module: "returns" },
  { key: "receiving-inventory", label: "Start Receiving", sub: "Receive inbound POs", icon: PackagePlus, to: "/receiving-inventory", accent: "blue", module: "inventory" },
  { key: "move-stock", label: "Move Stock", sub: "Relocate inventory", icon: ArrowLeftRight, to: "/move-stock", accent: "navy", module: "inventory" },
  { key: "delivery", label: "Live Tracking", sub: "Packages in motion", icon: Radio, to: "/delivery", accent: "green", module: "delivery" },
  { key: "console", label: "Ops Console", sub: "Accounts, users & more", icon: Boxes, to: "/admin", accent: "slate" },
];

const ACCENTS = {
  orange: "hover:border-bh-orange text-bh-orange bg-bh-orange/10",
  blue: "hover:border-bh-blue text-bh-blue bg-bh-blue/10",
  navy: "hover:border-bh-navy-deep text-bh-navy-deep bg-bh-navy-deep/10",
  green: "hover:border-emerald-500 text-emerald-600 bg-emerald-50",
  slate: "hover:border-slate-500 text-slate-600 bg-slate-100",
};

export default function EmployeeCockpit() {
  const navigate = useNavigate();
  const { user, isAdmin, setViewAs, can } = useAuth();
  const [searchParams] = useSearchParams();
  // Opt-in preview hook: /?previewStore=<accountId> scopes the embedded analytics
  // to one store so the panel renders fast (the default all-accounts aggregate is heavy).
  const previewStore = searchParams.get("previewStore") || null;
  const firstName = (user?.name || "").split(" ")[0];

  const tiles = TILES.filter((t) => !t.module || can(t.module, "view"));

  const actAsCustomer = () => {
    // Jump to the account switcher hint / open a customer view.
    navigate("/admin");
  };

  return (
    <div data-testid="employee-cockpit" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-bh-orange">
            <ShieldCheck className="w-3.5 h-3.5" /> Warehouse Cockpit
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-bh-navy-deep mt-2" style={{ fontFamily: "Manrope" }}>
            Welcome back, <span className="text-bh-blue">{firstName || "team"}</span>
          </h1>
          <p className="text-bh-body mt-1.5">Pick a station to launch, or scan the live board on the right — across every customer.</p>
        </div>
      </div>

      {/* Big touch action tiles */}
      <div data-testid="cockpit-tiles" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-5">
        {isAdmin && (
          <ActionTile
            testId="tile-act-as-customer"
            label="View as Customer"
            sub="Open a brand's portal"
            icon={UserCog}
            accent="violet"
            onClick={actAsCustomer}
          />
        )}
        {tiles.map((t) => (
          <ActionTile
            key={t.key}
            testId={`tile-${t.key}`}
            label={t.label}
            sub={t.sub}
            icon={t.icon}
            accent={t.accent}
            onClick={() => navigate(t.to)}
          />
        ))}
      </div>

      {/* Analytics + Live side panel */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
        <div className="xl:col-span-8 min-w-0">
          <Dashboard scopeAccountId={previewStore} />
        </div>
        <div className="xl:col-span-4">
          <LiveBoard onGo={navigate} onSetViewAs={setViewAs} isAdmin={isAdmin} />
        </div>
      </div>
    </div>
  );
}

function ActionTile({ label, sub, icon: Icon, accent = "blue", onClick, testId }) {
  const tone = {
    orange: "hover:border-bh-orange",
    blue: "hover:border-bh-blue",
    navy: "hover:border-bh-navy-deep",
    green: "hover:border-emerald-500",
    slate: "hover:border-slate-400",
    violet: "hover:border-violet-500",
  }[accent];
  const iconTone = {
    orange: "text-bh-orange bg-bh-orange/10",
    blue: "text-bh-blue bg-bh-blue/10",
    navy: "text-bh-navy-deep bg-bh-navy-deep/10",
    green: "text-emerald-600 bg-emerald-50",
    slate: "text-slate-600 bg-slate-100",
    violet: "text-violet-600 bg-violet-50",
  }[accent];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group flex flex-col items-start justify-between gap-6 p-6 min-h-[150px] bg-white rounded-2xl border-2 border-slate-100 ${tone} shadow-sm hover:shadow-lg transition-[box-shadow,border-color,transform] duration-200 active:scale-[0.98] text-left`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${iconTone}`}>
        <Icon className="w-7 h-7" strokeWidth={2.2} />
      </div>
      <div>
        <p className="text-lg font-bold text-bh-navy-deep leading-tight" style={{ fontFamily: "Manrope" }}>{label}</p>
        <p className="text-sm text-bh-muted mt-0.5">{sub}</p>
      </div>
    </button>
  );
}

const BOARD_ITEMS = [
  { key: "pending_users", label: "Users awaiting approval", icon: Users, tone: "orange", to: "/admin", section: "users" },
  { key: "open_claims", label: "Open claims to action", icon: ShieldAlert, tone: "red", to: "/admin", section: "claims" },
  { key: "returns_pending", label: "Returns awaiting disposition", icon: RotateCcw, tone: "blue", to: "/admin", section: "returns" },
  { key: "inbound_pos", label: "Inbound POs to receive", icon: Boxes, tone: "green", to: "/receiving-inventory" },
  { key: "customer_actions", label: "Recent customer actions", icon: Bell, tone: "violet", to: "/admin", section: "returns" },
];

const TONES = {
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-600",
  blue: "bg-blue-100 text-blue-600",
  green: "bg-emerald-100 text-emerald-600",
  violet: "bg-violet-100 text-violet-600",
};

function LiveBoard({ onGo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/staff/cockpit");
      setData(data);
    } catch (e) {
      // Non-blocking: the board is a live-refreshing widget; surface for debugging only.
      if (process.env.NODE_ENV !== "production") console.warn("Cockpit board load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(() => load({ silent: true }), 60_000);
    return () => clearInterval(i);
  }, [load]);

  return (
    <div data-testid="cockpit-live-board" className="bg-white rounded-2xl border border-slate-200 shadow-sm xl:sticky xl:top-24">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          <h2 className="text-lg font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>Things to do</h2>
        </div>
        <button
          data-testid="cockpit-board-refresh"
          onClick={() => load({ silent: true })}
          className="text-slate-400 hover:text-bh-blue p-1.5 rounded-md hover:bg-slate-50 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <p className="px-5 pt-3 text-xs text-bh-muted">
        Across {data?.total_accounts ?? "—"} customer{data?.total_accounts === 1 ? "" : "s"}
      </p>

      <ul className="p-3 space-y-1.5">
        {BOARD_ITEMS.map((it) => {
          const count = data?.[it.key];
          const active = count > 0;
          return (
            <li key={it.key}>
              <button
                type="button"
                data-testid={`board-item-${it.key}`}
                onClick={() => onGo(it.section ? `${it.to}?section=${it.section}` : it.to)}
                className={`w-full flex items-center gap-3 px-3 py-3.5 rounded-xl border transition-colors text-left ${
                  active ? "border-slate-200 hover:bg-slate-50" : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${TONES[it.tone]}`}>
                  <it.icon className="w-5 h-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-bh-navy-deep truncate">{it.label}</span>
                </span>
                <span className={`text-xl font-extrabold tabular-nums ${active ? "text-bh-navy-deep" : "text-slate-300"}`} style={{ fontFamily: "Manrope" }}>
                  {loading && count == null ? "…" : (count ?? 0)}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
